/**
 * listinfoProps.js — `listinfo.options.props` và `listinfo.default_condition`.
 *
 * `options.props` là một **SCRIPT trả về object thuộc tính** cho InputBase (`InputBase.js:658-673`):
 *
 *   new Function("Moment","API","utils","context","listInfo", `return (async ()=>{ ... })()`)
 *
 * Object đó đi qua `getProps(key)` và **THẮNG** mọi prop React cùng tên, nên nó là nơi người khai báo
 * chỉnh hành vi màn hình mà không phải sửa mã: điều kiện cứng, bộ lọc nhanh, số dòng một trang…
 *
 * `default_condition` cũng là script, cũng bind `Moment/API/utils/context`, cũng **tự thêm `return`**.
 *
 * ⚠️ Luật tự thêm `return` ở ĐÂY khác `createDefaults.js`: bộ cũ dò `indexOf("return")` (KHÔNG có dấu
 * cách), nên `"returnValue"` cũng bị coi là đã có `return`. Giữ nguyên cái dở đó vì cấu hình thật đang
 * chạy theo nó — đổi cho "đẹp" là đổi hành vi của những listinfo đang sống.
 *
 * File THUẦN (không import gì) để test node chạy trên chính mã nguồn; `Moment`/`utils` tiêm qua `deps`.
 */

/** Tên tham số của script `options.props` và `default_condition` (đúng thứ tự bộ cũ bind). */
const PROPS_ARG_NAMES = ['Moment', 'API', 'utils', 'context', 'listInfo'];

/** Khoá của `options.props` DỊCH ĐƯỢC sang data_list_config. Ngoài danh sách này -> console.warn. */
const SUPPORTED_PROPS = [
	'requiredCondition', 'dataRequired', 'defaultCondition',
	'filterQuickConfig', 'filterQuickConfigs',
	'rowsPerPage', 'limit', 'title', 'readOnly', 'hideActionAdd', 'hideHeader',
	'requireLogin', 'typeDisplay', 'sortTypes', 'optionsFormCode', 'fullScreen',
];

/**
 * Khoá KHÔNG dịch được nhưng cũng KHÔNG đáng cảnh báo: chúng chỉ có nghĩa với cách vẽ của bộ cũ
 * (style, slot React, cờ nội bộ), người khai báo không chờ đợi gì ở bộ mới.
 */
const SILENT_PROPS = ['mediaQueryMatches', 'isDetail', 'isWidget', 'isPicker', 'master', 'code', 'form'];

/** Thêm `return` nếu script không có — ĐÚNG luật của bộ cũ (dò `"return"`, không có dấu cách). */
function scriptBody(raw) {
	if (raw === undefined || raw === null) return '';
	const s = String(raw).trim();
	if (!s) return '';
	return s.indexOf('return') >= 0 ? s : 'return ' + s;
}

/**
 * Chạy một script kiểu `options.props` / `default_condition`.
 * @param args { context, listInfo }
 * @param deps { Moment, utils }
 * @returns {{ ok: boolean, value: any, error: string|null }}
 */
async function runListinfoScript(raw, args, deps) {
	const body = scriptBody(raw);
	if (!body) return { ok: false, value: null, error: null };
	const a = args || {};
	const d = deps || {};
	const context = a.context || {};
	try {
		const fn = new Function(...PROPS_ARG_NAMES, 'return (async () => {\n' + body + '\n})()');
		const value = await fn(d.Moment || null, context.apis || null, d.utils || null, context, a.listInfo || null);
		return { ok: true, value, error: null };
	} catch (e) {
		return { ok: false, value: null, error: (e && e.message) || String(e) };
	}
}

// =============================================================================
// HEADER DẠNG SCRIPT của field listinfo
// -----------------------------------------------------------------------------
// Một số field khai `header` là SCRIPT để lấy nhãn thật theo cấu hình của công ty, vd:
//   let kieu = await API.asyncGetData(context.userInfo.token,"groupkind",{group_kind:1,group_type:'DMVT'},null,false,true);
//   if(kieu) return kieu.group_kind_name; return "Nhóm vật tư"
// Không chạy thì cột/ô hiện NGUYÊN VĂN đoạn mã làm nhãn.
// Phương ngữ: đúng InputBase.js (~dòng 708) / FormBase.prepareFields —
//   - nhận diện: chuỗi CÓ `return ` (có dấu cách);
//   - bọc async (được `await`), KHÔNG tự thêm `return`;
//   - tham số: master, Moment, Numeral, API, utils, context, field, listInfo (master = {} lúc dựng cấu hình).
// =============================================================================
const HEADER_ARG_NAMES = ['master', 'Moment', 'Numeral', 'API', 'utils', 'context', 'field', 'listInfo'];

function isScriptHeader(header) {
	return typeof header === 'string' && header.indexOf('return ') >= 0;
}

/**
 * Chạy mọi `header` dạng script của một mảng field listinfo. Trả mảng MỚI (không sửa mảng vào):
 * field có header script được thay `header` bằng nhãn thật và giữ bản gốc ở `header_source` (đúng tên
 * InputBase dùng). Script lỗi / trả rỗng -> nhãn lùi về `name` của field, KHÔNG để đoạn mã lên màn hình.
 *
 * @param fields  mảng field listinfo (dùng `header`, `name`)
 * @param args    { context, listInfo, master }
 * @param deps    { Moment, Numeral, utils, cache?: Map, cacheKey?: string }
 *                `cache`: cùng một script lặp lại ở nhiều field/listinfo (vd nhãn "Nhóm vật tư" ở mọi
 *                chứng từ kho) chỉ chạy MỘT lần; khoá = cacheKey (vd id_app) + nội dung script.
 * @returns {Promise<{ fields: object[], errors: {field:string, error:string}[] }>}
 */
async function resolveScriptHeaders(fields, args, deps) {
	const list = Array.isArray(fields) ? fields : [];
	const errors = [];
	if (!list.some(f => f && isScriptHeader(f.header))) return { fields: list, errors };
	const a = args || {};
	const d = deps || {};
	const context = a.context || {};
	const cache = d.cache instanceof Map ? d.cache : null;
	const out = await Promise.all(list.map(async (f) => {
		if (!f || !isScriptHeader(f.header)) return f;
		const src = f.header;
		const fallback = f.name || '';
		const run = async () => {
			const fn = new Function(...HEADER_ARG_NAMES, 'return (async () => {\n' + src + '\n})()');
			return fn(a.master || {}, d.Moment || null, d.Numeral || null, context.apis || null, d.utils || null, context, f, a.listInfo || null);
		};
		let label;
		try {
			const key = cache ? (d.cacheKey || '') + '\u0000' + src : null;
			let p = key !== null ? cache.get(key) : null;
			if (!p) {
				p = run();
				if (key !== null) {
					cache.set(key, p);
					p.catch(() => cache.delete(key));   // lỗi thì lần sau thử lại, không nhớ lỗi mãi
				}
			}
			label = await p;
		} catch (e) {
			errors.push({ field: fallback, error: (e && e.message) || String(e) });
			label = undefined;
		}
		if (label === undefined || label === null || label === '' || typeof label === 'object') label = fallback;
		return { ...f, header: String(label), header_source: src };
	}));
	return { fields: out, errors };
}

/**
 * Lọc `props.defaultCondition` đúng như `InputBase.getDefaultConditon()`.
 *
 * Chỉ giữ `_id`, `$or`, `$and` và các field THẬT (không `is_tmp`). Field tạm không được lưu xuống
 * database, nên lọc theo nó là gửi cho server một điều kiện không bao giờ khớp — danh sách rỗng mà
 * không có lỗi nào. `cType:"$or"` thì gói các khoá còn lại thành `$or`.
 */
function filterDefaultCondition(df, fields) {
	if (!df || typeof df !== 'object') return {};
	const list = fields || [];
	const out = {};
	for (const key in df) {
		if (!Object.prototype.hasOwnProperty.call(df, key)) continue;
		const fieldCfg = list.find(f => f && f.name === key);
		if (key === '_id' || key === '$or' || key === '$and' || (fieldCfg && !fieldCfg.is_tmp)) out[key] = df[key];
	}
	if (df.cType === '$or' && Object.keys(out).length > 0) {
		return { $or: Object.keys(out).map(k => ({ [k]: out[k] })) };
	}
	return out;
}

/**
 * Một mục `filterQuickConfigs` của bộ cũ -> cấu hình bộ lọc nhanh của DataListManager.
 *
 *   { defaultLabel, api, field, field_ref, field_label, condition, defaultChoose }
 *      -> { allLabel, api, field, apiMapping:{value,label}, apiCondition, defaultValue }
 *
 * `field` là cột LỌC trên bản ghi chính, `field_ref`/`field_label` là cột GIÁ TRỊ/NHÃN trong danh mục
 * được nạp. Lẫn hai thứ này là lọc bằng nhãn tiếng Việt và không ra dòng nào.
 */
function adaptQuickFilter(cfg) {
	if (!cfg || typeof cfg !== 'object' || !cfg.field) return null;
	const out = { field: cfg.field };
	if (cfg.defaultLabel) out.allLabel = cfg.defaultLabel;
	if (cfg.api) out.api = cfg.api;
	// Cột giá trị/nhãn đúng như FilterQuick.js: `item[field_ref || field]` và
	// `item[field_label || field_ref || field]`. Bản trước lùi về `_id`/`name` khi thiếu `field_ref` —
	// cấu hình cũ chỉ khai `field` thì chip mang `_id` của danh mục đi lọc cột mã -> không ra dòng nào.
	if (cfg.api && cfg.api !== 'Date' && cfg.api !== 'DateTime') {
		out.apiMapping = { value: cfg.field_ref || cfg.field, label: cfg.field_label || cfg.field_ref || cfg.field };
	}
	if (cfg.condition && typeof cfg.condition === 'object') out.apiCondition = cfg.condition;
	if (Array.isArray(cfg.options) && cfg.options.length) out.options = cfg.options;
	if (cfg.defaultChoose !== undefined) out.defaultValue = cfg.defaultChoose;
	// Các khoá hành vi còn lại của FilterQuick.js — xem quickFilterRules.js.
	if (cfg.multiSel === true) out.multiSel = true;
	if (cfg.selectAll === false) out.selectAll = false;
	if (typeof cfg.title === 'string' && cfg.title.trim()) out.title = cfg.title.trim();
	if (cfg.label && typeof cfg.label === 'string') out.title = out.title || cfg.label;
	// `counterCode` của bộ cũ là mã listinfo để ĐẾM; InputBase luôn truyền chính api_code của màn hình
	// -> bộ mới đếm trên chính API của danh sách, chỉ cần biết có bật hay không.
	if (cfg.counterCode) out.showCount = true;
	if (typeof cfg.prepareQuery === 'function') out.prepareQuery = cfg.prepareQuery;
	if (typeof cfg.prepareData === 'function') out.prepareData = cfg.prepareData;
	return out;
}

/** `sortTypes` của bộ cũ (`[{name,value}]`) -> `sortOptions` (`[{label,sort}]`). */
function adaptSortTypes(sortTypes, parseSort) {
	if (!Array.isArray(sortTypes) || !sortTypes.length) return null;
	const out = [];
	for (const s of sortTypes) {
		if (!s) continue;
		let sort = null;
		if (s.value && typeof s.value === 'object') sort = s.value;
		else if (typeof s.value === 'string' && typeof parseSort === 'function') sort = parseSort(s.value);
		if (!sort || !Object.keys(sort).length) continue;
		out.push({ label: s.name || s.key || 'Sắp xếp', sort });
	}
	return out.length ? out : null;
}

/**
 * Dịch object `options.props` ĐÃ CHẠY sang phần cấu hình chồng lên cấu hình dịch từ listinfo.
 *
 * @param props   object trả về từ script
 * @param li      listinfo (cần `fields` để lọc defaultCondition)
 * @param deps    { parseSort } — dùng lại bộ đọc chuỗi "cot chieu" của adaptListinfoConfig
 * @returns {{ config, condition, applied: string[], ignored: string[] }}
 *          `condition` tách riêng vì nó phải TRỘN chứ không ghi đè.
 */
function adaptListinfoProps(props, li, deps) {
	const rs = { config: {}, condition: {}, applied: [], ignored: [] };
	if (!props || typeof props !== 'object') return rs;
	const c = rs.config;
	const mark = (k) => { if (rs.applied.indexOf(k) < 0) rs.applied.push(k); };

	// --- ĐIỀU KIỆN CỨNG: trộn theo đúng thứ tự của InputBase.js:1035 ---
	for (const key of ['requiredCondition', 'dataRequired']) {
		if (props[key] && typeof props[key] === 'object') { Object.assign(rs.condition, props[key]); mark(key); }
	}
	if (props.defaultCondition && typeof props.defaultCondition === 'object') {
		Object.assign(rs.condition, filterDefaultCondition(props.defaultCondition, (li || {}).fields));
		mark('defaultCondition');
	}

	// --- BỘ LỌC NHANH ---
	const quick = [];
	if (props.filterQuickConfig) {
		const one = adaptQuickFilter(props.filterQuickConfig);
		if (one) { quick.push(one); mark('filterQuickConfig'); }
	}
	if (Array.isArray(props.filterQuickConfigs)) {
		for (const cfg of props.filterQuickConfigs) {
			const one = adaptQuickFilter(cfg);
			if (one) quick.push(one);
		}
		if (props.filterQuickConfigs.length) mark('filterQuickConfigs');
	}
	if (quick.length) {
		c.quickFilterConfigs = quick;
		// Giữ luôn khoá số ít để cấu hình cũ và mọi chỗ đọc `quickFilterConfig` vẫn chạy.
		c.quickFilterConfig = quick[0];
	}

	// --- CÁC KHOÁ ĐƠN ---
	const limit = props.rowsPerPage || props.limit;
	if (Number(limit) > 0) { c.limit = Number(limit); mark(props.rowsPerPage ? 'rowsPerPage' : 'limit'); }
	if (typeof props.title === 'string' && props.title.trim()) { c.title = props.title.trim(); mark('title'); }
	if (props.requireLogin === true) { c.requireLogin = true; mark('requireLogin'); }
	if (props.hideHeader === true) { c.hideHeader = true; mark('hideHeader'); }
	if (props.optionsFormCode) { c.codeFormOption = props.optionsFormCode; mark('optionsFormCode'); }
	if (props.fullScreen === true) { c.formSize = 'full'; mark('fullScreen'); }

	// `readOnly` của bộ cũ khoá CẢ BA hành động; `hideActionAdd` chỉ ẩn nút thêm.
	if (props.readOnly === true) { c.canAdd = false; c.canEdit = false; c.canDelete = false; mark('readOnly'); }
	if (props.hideActionAdd === true) { c.canAdd = false; mark('hideActionAdd'); }

	if (props.typeDisplay === 'GridView') { c.defaultViewMode = 'card'; mark('typeDisplay'); }
	else if (props.typeDisplay === 'ListView') { c.defaultViewMode = 'table'; mark('typeDisplay'); }

	const sortOptions = adaptSortTypes(props.sortTypes, deps && deps.parseSort);
	if (sortOptions) { c.sortOptions = sortOptions; mark('sortTypes'); }

	// --- CÒN LẠI: liệt kê để cảnh báo ---
	for (const key in props) {
		if (!Object.prototype.hasOwnProperty.call(props, key)) continue;
		if (rs.applied.indexOf(key) >= 0) continue;
		if (SUPPORTED_PROPS.indexOf(key) >= 0) continue;   // có hỗ trợ nhưng giá trị không dùng được
		if (SILENT_PROPS.indexOf(key) >= 0) continue;
		rs.ignored.push(key);
	}
	return rs;
}

/**
 * Chuẩn hoá danh sách bộ lọc nhanh: nhận cả khoá số nhiều (mới) lẫn số ít (cũ), bỏ cấu hình không đủ
 * dữ liệu để vẽ (thiếu `field`, hoặc không có nguồn giá trị nào).
 */
function normalizeQuickFilters(configs, single) {
	const list = [];
	const push = (c) => {
		if (!c || !c.field) return;
		if (!c.api && !(Array.isArray(c.options) && c.options.length)) return;
		if (list.some(x => x.field === c.field)) return;   // hai hàng lọc cùng một cột thì hàng sau vô nghĩa
		list.push(c);
	};
	if (Array.isArray(configs)) for (const c of configs) push(c);
	push(single);
	return list;
}

/** Giá trị khởi tạo của từng hàng lọc nhanh (`defaultChoose` của bộ cũ). */
function initialQuickFilterValues(list) {
	const out = {};
	for (const c of list || []) out[c.field] = c.defaultValue !== undefined ? c.defaultValue : '';
	return out;
}

/**
 * Giá trị một chip lọc nhanh -> giá trị đem đi lọc.
 *
 * ⚠️ Chỉ chuỗi CÓ `return ` mới là script. Bản trước viết `if(value.toString().indexOf("return "))`,
 * mà `indexOf` trả -1 khi KHÔNG có — tức là TRUTHY, nên mọi giá trị đều bị đem chạy: chip boolean
 * `"true"` biên dịch thành hàm không có `return`, trả `undefined`, và điều kiện lọc thành
 * `{trang_thai: undefined}` — danh sách sai mà chỉ có một dòng đỏ ở console.
 */
function quickFilterValue(raw, context) {
	let v = raw;
	if (v === 'true' || v === 'True') return true;
	if (v === 'false' || v === 'False') return false;
	if (typeof v !== 'string' || v.indexOf('return ') < 0) return v;
	try {
		return new Function('context', v)(context);
	} catch (e) {
		console.error('[listinfoProps] lỗi chạy giá trị lọc nhanh:', (e && e.message) || e, raw);
		return raw;
	}
}

export {
	PROPS_ARG_NAMES, SUPPORTED_PROPS, SILENT_PROPS,
	normalizeQuickFilters, initialQuickFilterValues, quickFilterValue,
	HEADER_ARG_NAMES, isScriptHeader, resolveScriptHeaders,
	scriptBody, runListinfoScript, filterDefaultCondition,
	adaptQuickFilter, adaptSortTypes, adaptListinfoProps,
};
